import React from 'react';
import { createPortal } from 'react-dom';
import { getDocumentIcon } from './seachpage-helpers';
import { PdfCanvasViewer } from './PdfCanvasViewer';
import { FaDownload, FaExternalLinkAlt, FaTimes } from 'react-icons/fa';
import { DocxViewer } from './DocxViewer';
import { SpreadsheetViewer } from './SpreadsheetViewer';
import { EmlViewer } from './EmlViewer';
import { MsgViewer } from './MsgViewer';

export interface PreviewDoc { uri: string; extension: string; documentType?: string; highlightText?: string | null }
export function DocumentPreviewModal({ doc, onClose }: { doc: PreviewDoc | null; onClose: () => void }) {
  if (!doc) return null;
  let ext = (doc.extension || '').toLowerCase();
  if (!ext && doc.uri) {
    const m = doc.uri.match(/\.([a-zA-Z0-9]+)$/); ext = m?.[1]?.toLowerCase()||'';
  }
  if (ext.startsWith('.')) ext = ext.slice(1);
  const isPdf = ext === 'pdf';
  const isImage = ['jpg','jpeg','png','gif'].includes(ext);
  const isEml = ext === 'eml';
  const isMsg = ext === 'msg';
  const isDocx = ext === 'docx';
  const isSheet = ext === 'xlsx' || ext === 'xls';

  const previewable = isPdf || isImage || isEml || isMsg || isDocx || isSheet;

  function guessFileName() {
    try {
      const u = new URL(doc.uri, window.location.origin);
      const last = (u.pathname.split('/').filter(Boolean).pop() || '').trim();
      if (last) return last;
    } catch {
      // ignore
    }
    return `document.${ext || 'bin'}`;
  }

  async function download() {
    try {
      const resp = await fetch(doc.uri);
      if (!resp.ok) throw new Error('Failed to fetch file');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = guessFileName();
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch {
      // Fallback: open in new tab; user can use browser download.
      window.open(doc.uri, '_blank');
    }
  }

  function openInNewTab() {
    window.open(doc.uri, '_blank');
  }

  const modal = (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }} onClick={onClose}>
      <div
        style={{
          background:'#fff',
          borderRadius:8,
          minWidth:700,
          minHeight:500,
          maxWidth:'95vw',
          maxHeight:'95vh',
          position:'relative',
          boxShadow:'0 4px 10px rgba(0,0,0,0.2)',
          display:'flex',
          flexDirection:'column',
          overflow:'hidden'
        }}
        onClick={e=>e.stopPropagation()}
      >
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 12px', background:'#f7fafd', borderBottom:'1px solid #e0e6ed' }}>
          <div style={{ fontWeight:700, color:'#2d3e50' }}>Preview</div>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            {previewable && (
              <>
                <button
                  onClick={openInNewTab}
                  title="Open in new tab"
                  aria-label="Open in new tab"
                  style={{ background:'#eee', border:'none', borderRadius:4, width:32, height:28, cursor:'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center' }}
                >
                  <FaExternalLinkAlt size={14} />
                </button>
                <button
                  onClick={download}
                  title="Download"
                  aria-label="Download"
                  style={{ background:'#eee', border:'none', borderRadius:4, width:32, height:28, cursor:'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center' }}
                >
                  <FaDownload size={14} />
                </button>
              </>
            )}
            <button
              onClick={onClose}
              title="Close"
              aria-label="Close"
              style={{ background:'#eee', border:'none', borderRadius:4, width:32, height:28, cursor:'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center' }}
            >
              <FaTimes size={16} />
            </button>
          </div>
        </div>

        <div style={{ padding:10, flex:1, minHeight:0, display:'flex' }}>
          {previewable ? (
            isPdf
              ? <div style={{ flex:1, minHeight:0 }}><PdfCanvasViewer url={doc.uri} highlightText={doc.highlightText} height={'100%'} /></div>
              : isImage
                ? <div style={{ flex:1, minHeight:0, overflow:'auto' }}><img src={doc.uri} alt="Preview" style={{ maxWidth:'100%', maxHeight:'100%', display:'block', margin:'0 auto' }} /></div>
                : isEml
                  ? <div style={{ flex:1, minHeight:0 }}><EmlViewer url={doc.uri} height={'100%'} highlightText={doc.highlightText} /></div>
                  : isMsg
                    ? <div style={{ flex:1, minHeight:0 }}><MsgViewer url={doc.uri} height={'100%'} highlightText={doc.highlightText} /></div>
                    : isDocx
                      ? <div style={{ flex:1, minHeight:0 }}><DocxViewer url={doc.uri} height={'100%'} highlightText={doc.highlightText} /></div>
                      : isSheet
                        ? <div style={{ flex:1, minHeight:0 }}><SpreadsheetViewer url={doc.uri} height={'100%'} highlightText={doc.highlightText} /></div>
                        : <div style={{ padding:24, textAlign:'center' }}>Preview not available for: <b>{doc.documentType || ext || 'unknown'}</b></div>
          ) : (
            <div style={{ padding:24, textAlign:'center' }}>Preview not available for: <b>{doc.documentType || ext || 'unknown'}</b></div>
          )}
        </div>
      </div>
    </div>
  );

  // Render modal into document.body so it's outside the scaled app container and not affected by ui-scale transform
  return createPortal(modal, document.body);
}
